'use client';

import * as Sentry from '@sentry/react';
import { createContext, ReactNode, useContext, useEffect, useMemo, useState } from 'react';

import { CookiesKeys } from '../../constants/cookiesKeys';
import { Environment } from '../../constants/environments';
import { HttpVerb } from '../../constants/httpVerbs';
import { Biometry } from '../../constants/producers';
import { HolderProps } from '../../types/cart';
import { User } from '../../types/user';
import { clearCookiesExceptTrustedDevice } from '../../utils/clearCookiesExceptTrustedDevice';
import getCookieValue from '../../utils/getCookieValue';
import getIngCookieValue from '../../utils/getIngCookies';
import { logger } from '../../utils/logger';
import { removeCookieValues } from '../../utils/removeCookie';
import setCookieValues from '../../utils/setCookieValue';
import { shouldRenewToken, isTokenExpired } from '../../utils/tokenValidation';
import { useApiService } from '../useApiService';
import { canSkipFacialRecognitionFetch } from './helpers/faceRecognition';
import { useLoaderScreenContext } from './loader-screen.context';

export type UserToIdentifyTicket = {
  id: number;
  name: string;
  picture: string | null;
  email: string;
};

type GetUSerByEmailOrDocument = {
  email?: string;
  document?: string;
  checkRfc: boolean;
  eventId?: number;
};

type GetRfcProps = {
  email?: string;
  document?: string;
  eventId: number;
};

export type UserContextProps = {
  user: User | null;
  loading: boolean;
  rfData: rfDataProps | null;
  rFCheckWasSuccessful: boolean;
  authenticatedUser: boolean | null;
  errorWhenTryingToRegisterRf: boolean;
  userAlreadyHasRegisteredFace: boolean;
  userHasBiometry: boolean;
  hasSsoAuth: boolean;
  updateUser: (updatedUser: User) => void;
  tryAgainRC: () => void;
  renewToken: () => Promise<void>;
  ssoAuth: (openidToken: string) => void;
  pollFacialRecognitionStatus: ({ eventId }: { eventId: number }) => void;
  getFacialRecognitionUrl: (
    args: { eventId: number, producerId: number }
  ) => Promise<rfDataProps | null>;
  getUserByEmailOrDocument: ({ email, document }: GetUSerByEmailOrDocument) => Promise<HolderProps[]>;
  handleAuthenticatedUser: (isAuthenticated: boolean) => void;
  setSsoAuth: (hasSsoAuth: boolean) => void;
}

type ResponseDataUser = {
  responseData: User;
};

type ResponseRenewToken = {
  responseData: {
    authToken: string;
    device: {
      id: number;
      name: string;
      creationdate: string;
      verified: boolean;
    }
  };
};

type ResponseSso = {
  responseData: {
    data: {
      authToken: string;
      device: {
        id: number;
        name: string;
        creationdate: string;
        verified: boolean;
      };
      userId?: number;
      token?: string;
    };
  }
};

type ResponseDataUserByEmail = {
  responseData: UserToIdentifyTicket[];
};

type rfDataProps = {
  status: string;
  onboarding_url: string;
  timestamp_rfc: string;
  tokenFaceRecognition?: string;
  token: string;
}

type ResponseGetFacialRecognitionUrl = {
  responseData: {
    data: rfDataProps & {
      hasBiometry: boolean;
    }
  }
};

type DataRequesteRfUrl = {
  callbackURL: string,
  eventId: number
}

const UserContext = createContext({} as UserContextProps);

function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasSsoAuth, setSsoAuth] = useState(false);
  const [authenticatedUser, setAuthenticatedUser] = useState(false);
  const [userAlreadyHasRegisteredFace, setUserAlreadyHasRegisteredFace] = useState(false);
  const [userHasBiometry, setUserHasBiometry] = useState(false);
  const [rFCheckWasSuccessful, setRFCheckWasSuccessful] = useState(false);
  const [errorWhenTryingToRegisterRf, setErrorWhenTryingToRegisterRf] = useState(false);
  const [rfData, setRfData] = useState<rfDataProps | null>(null);

  const { ENVIRONMENT } = Environment;
  const environmentSuffix = ENVIRONMENT && ENVIRONMENT !== 'production' ? `_${String(ENVIRONMENT)}_` : '_';

  const jwtCookieKey = `ing_1${environmentSuffix}${encodeURIComponent('jwt')}`;
  const tokenCookieKey = `ing_1${environmentSuffix}${encodeURIComponent('token')}`;
  const trustedDeviceCookieKey = `ing_1${environmentSuffix}${encodeURIComponent('trusted_device')}`;
  const userDataCookieKey = `ing_1${environmentSuffix}${encodeURIComponent('userData')}`;
  const userIdCookieKey = `ing_1${environmentSuffix}${encodeURIComponent('userId')}`;

  const userId = getIngCookieValue('userId');
  const userToken = getIngCookieValue('token');

  const clearCredentials = () => {
    removeCookieValues({
      keys : [
        jwtCookieKey,
        tokenCookieKey,
        trustedDeviceCookieKey,
        userDataCookieKey,
        userIdCookieKey
      ]
    });
  };

  const renewToken = async (): Promise<void> => {
    if (!userToken) return;

    const currentJwtToken = getIngCookieValue('jwt');

    if (isTokenExpired(currentJwtToken ?? null)) {
      logger.warn('Token has expired, clearing all cookies except trusted_device');
      clearCookiesExceptTrustedDevice();
      setUser(null);
      setLoading(false);
      return;
    }

    if (!shouldRenewToken(currentJwtToken ?? null)) {
      logger.info('Token is still valid, skipping renew');
      return;
    }

    try {
      setLoading(true);
      const resourcePath: string = `login/renew-token?apikey=${Environment.API_KEY}&usertoken=${userToken}`;
      const { responseData } = await request<null, ResponseRenewToken>({
        requestOptions : { resourcePath },
        type : HttpVerb.GET,
      }, { baseUrl : Environment.API_URL });

      if (responseData && responseData.authToken) {
        setCookieValues([{
          key : jwtCookieKey,
          value : responseData.authToken
        }]);
        logger.info('Token renewed successfully');
        return;
      }
      clearCredentials();
    } catch (error) {
      clearCredentials();
      logger.error(error as string);
    } finally {
      setLoading(false);
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);

    const tokenCookieKey = `ing_1${environmentSuffix}${encodeURIComponent('token')}`;
    const jwtCookieKey = `ing_1${environmentSuffix}${encodeURIComponent('jwt')}`;
    setCookieValues([
      { key : jwtCookieKey, value : updatedUser.authToken || '', },
      { key : tokenCookieKey, value : updatedUser.token }
    ], true);
  };

  const handleAuthenticatedUser = (isAuthenticated: boolean) => {
    setAuthenticatedUser(isAuthenticated);
  };

  const { toggleLoader } = useLoaderScreenContext();
  const { request } = useApiService();

  /**
   * Updates the browser's URL by removing a specific query parameter.
   * @param {string} param - The name of the parameter to be removed.
   * @description This function uses the `URL` API to manipulate the current page's URL.
   * The provided parameter will be removed from the query string, and the browser's URL
   * will be updated without reloading the page by using the `history.replaceState` method.
   *
   * @example
   * // Current URL: https://example.com?page=1&sort=asc
   * updateURLAndRemoveParameter('sort');
   * // Updated URL: https://example.com?page=1
   *
   * @example
   * // Current URL: https://example.com?search=apple&category=fruit
   * updateURLAndRemoveParameter('search');
   * // Updated URL: https://example.com?category=fruit
  */
  function updateURLAndRemoveParameter(param: string) {
    const urlObj = new URL(window.location.href);

    urlObj.searchParams.delete(param);

    window.history.replaceState({}, document.title, urlObj.toString());
  }

  const ssoAuth = async (openidToken: string) => {
    try {
      setLoading(true);
      const resourcePath: string = `oauth/sso?apikey=${Environment.API_KEY}`;
      const { responseData } = await request<{ openidToken: string }, ResponseSso>({
        requestOptions : {
          resourcePath,
          data : { openidToken : openidToken }
        },
        type : HttpVerb.POST,
      }, { baseUrl : Environment.API_URL });
      const tokenCookieKey = `ing_1${environmentSuffix}${encodeURIComponent('token')}`;
      const userIdCookieKey = `ing_1${environmentSuffix}${encodeURIComponent('userId')}`;
      const trustedDeviceCookieKey = `ing_1${environmentSuffix}${encodeURIComponent('trusted_device')}`;
      const jwtCookieKey = `ing_1${environmentSuffix}${encodeURIComponent('jwt')}`;
      if (responseData.data.token && responseData.data.userId && responseData.data.device) {
        clearCredentials();
        setCookieValues([
          { key : userIdCookieKey, value : String(responseData.data.userId) },
          { key : jwtCookieKey, value : responseData.data.authToken },
          { key : tokenCookieKey, value : responseData.data.token },
          { key : trustedDeviceCookieKey, value : encodeURIComponent(JSON.stringify(responseData.data.device)) }
        ], true);
        setSsoAuth(true);
      }
    } catch (error) {
      logger.error(error as string);
    } finally {
      updateURLAndRemoveParameter('openidToken');
      setLoading(false);
    }
  };

  /**
   * Fetches user data from the API.
   * This asynchronous function performs the following actions:
   * - Checks if `userId` and `userToken` are available. If not, it returns immediately.
   * - Sets the loading state to `true` before making the API request.
   * - Creates an instance of `ApiService` with the base URL from the environment configuration.
   * - Constructs the resource path for the API request using `userId` and `userToken`.
   * - Makes an API request to fetch the user data.
   * - Sets the user data to the state if the request is successful.
   * - Logs an error message to the console if the request fails.
   * - Sets the loading state to `false` after the API request completes, regardless of success or failure.
   * @returns Promise<void> - A promise that resolves when the operation is complete.
  */
  const getUser = async () => {
    try {
      setLoading(true);
      const resourcePath: string = `users/${userId}?apikey=${Environment.API_KEY}&usertoken=${userToken}`;
      const { responseData } = await request<null, ResponseDataUser>({
        requestOptions : { resourcePath },
        type : HttpVerb.GET,
      }, { baseUrl : Environment.API_URL });
      setUser(responseData);
    } catch (error) {
      logger.error(error as string);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetches user details by email or document and returns an array of holder properties.
   * @param {Object} props - The properties for the function.
   * @param {string} [props.email] - The email of the user to search for.
   * @param {string} [props.document] - The document of the user to search for.
   * @returns {Promise<HolderProps[]>} A promise that resolves to an array of holder properties.
  */
  const getUserByEmailOrDocument = async (props: GetUSerByEmailOrDocument): Promise<HolderProps[]> => {
    const { email, document, checkRfc } = props;
    const documentParam = document ? `&document=${document}` : '';
    const emailParam = email ? `&term=${email}` : '';
    try {
      setLoading(true);
      toggleLoader({ show : true });
      const userToken = getCookieValue({ cookieKey : CookiesKeys.TOKEN });
      const resourcePath = '/search/transfer/user' +
        `?apikey=${Environment.API_KEY}${emailParam}${documentParam}` +
        `&checkrfc=${checkRfc}&usertoken=${userToken}&eventId=${props.eventId}`;

      const { responseData } = await request<null, ResponseDataUserByEmail>({
        requestOptions : { resourcePath },
        type : HttpVerb.GET,
      }, { baseUrl : Environment.API_URL, needToken : true });
      toggleLoader({ show : false });

      return responseData;
    } catch (error) {
      logger.error(error as string);
      return [];
    } finally {
      setLoading(false);
      toggleLoader({ show : false });
    }
  };

  const getRFCConfirmation = async (props: GetRfcProps): Promise<HolderProps[]> => {

    try {
      setLoading(true);
      const userToken = getCookieValue({ cookieKey : CookiesKeys.TOKEN });
      const resourcePath = 'search/transfer/user?apikey=' +
        `${Environment.API_KEY}&term=${props.email}&document=${props.document}` +
        `&usertoken=${userToken}&eventId=${props.eventId}&checkrfc=true`;

      const { responseData } = await request<null, ResponseDataUserByEmail>({
        requestOptions : { resourcePath },
        type : HttpVerb.GET,
      }, { baseUrl : Environment.API_URL, needToken : true });
      return responseData;
    } catch (error) {
      logger.error(error as string);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const getFacialRecognitionUrl = async (
    { eventId, producerId }: { eventId: number, producerId: number }
  ): Promise<rfDataProps | null> => {
    if (!authenticatedUser) return null;
    if (canSkipFacialRecognitionFetch({ userHasBiometry, rfData })) return rfData;
    try {
      const userToken = getCookieValue({ cookieKey : CookiesKeys.TOKEN });
      const resourcePath = `/rfc?apikey=${Environment.API_KEY}&usertoken=${userToken}&eventId=${eventId}`;

      const { responseData : { data } } = await request<DataRequesteRfUrl, ResponseGetFacialRecognitionUrl>({
        requestOptions : {
          resourcePath,
          data : { callbackURL : '#close', eventId }
        },
        type : HttpVerb.GET,
      }, { baseUrl : `${Environment.API_URL}` });
      //if the user dont have the token, we need to create a onboarding url
      if (!data.token && producerId) {
        return await createFacialRecognitionUrl({ eventId, producerId });
      }
      setRfData(data);
      setUserAlreadyHasRegisteredFace(Boolean(data && data.tokenFaceRecognition) ?? false);
      setUserHasBiometry(Boolean(data && data.hasBiometry) ?? false);
      return data;
    } catch (error) {
      Sentry.addBreadcrumb({
        category : 'facial-recognition',
        level : 'error',
        message : 'getFacialRecognitionUrl failed',
        data : { eventId, producerId },
      });
      logger.error('getFacialRecognitionUrl failed', { reason : error instanceof Error ? error.message : 'unknown' });
      return null;
    }
  };

  const createFacialRecognitionUrl = async (
    { eventId, producerId }: { eventId: number, producerId: number }
  ): Promise<rfDataProps | null> => {

    if (!authenticatedUser) return null;
    try {
      const userToken = getCookieValue({ cookieKey : CookiesKeys.TOKEN });
      const resourcePath = `/rfc?apikey=${Environment.API_KEY}&usertoken=${userToken}`;

      const { responseData : { data } } = await request<DataRequesteRfUrl, ResponseGetFacialRecognitionUrl>({
        requestOptions : {
          resourcePath,
          data : { callbackURL : Biometry.Imply.includes(producerId) ? window.origin + '/close' : '#close', eventId }
        },
        type : HttpVerb.POST,
      }, { baseUrl : `${Environment.API_URL}` });

      setRfData(data);

      setUserAlreadyHasRegisteredFace(Boolean(data && data.tokenFaceRecognition) ?? false);
      setUserHasBiometry(Boolean(data && data.hasBiometry) ?? false);
      return data;
    } catch (error) {
      Sentry.addBreadcrumb({
        category : 'facial-recognition',
        level : 'error',
        message : 'createFacialRecognitionUrl failed',
        data : { eventId, producerId },
      });
      logger.error('createFacialRecognitionUrl failed', {
        reason : error instanceof Error ? error.message : 'unknown',
      });
      return null;
    }
  };

  const tryAgainRC = () => {
    setErrorWhenTryingToRegisterRf(false);
    setRFCheckWasSuccessful(false);
    // This call is not necessary in the component in useEffect
    // pollFacialRecognitionStatus({ eventId });
  };

  const pollFacialRecognitionStatus = ({ eventId }: { eventId: number }): () => void => {
    let timedOut = false;

    if (!user) return () => {};

    const intervalId = setInterval(async () => {
      if (timedOut) return;
      const status = await getRFCConfirmation({ document : user.document.number, email : user.email, eventId });

      if (status.length > 0 && status[0].tokenFaceRecognition) {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
        setRFCheckWasSuccessful(true);
        logger.info('Facial recognition successful.');
        return;
      }

      logger.info('Still waiting for facial recognition...');
      setRFCheckWasSuccessful(false);
    }, 10000);

    const timeoutId = setTimeout(() => {
      timedOut = true;
      clearInterval(intervalId);
      setErrorWhenTryingToRegisterRf(true);
      logger.error('Failed to get facial recognition within the timeout period.');
    }, 600000);

    return () => {
      clearInterval(intervalId);
      clearTimeout(timeoutId);
    };
  };

  useEffect(() => {
    if (userId && userToken) {
      getUser();
    }
  }, [userId]);

  useEffect(() => {
    if (window.IngresseCheckout && user) {
      window.IngresseCheckout.trackSession(Environment.SIFT_KEY, 1, user.id);
    }
  }, [user]);

  const shared = useMemo(() => ({
    user, loading, authenticatedUser, rfData, getFacialRecognitionUrl, tryAgainRC,
    pollFacialRecognitionStatus, rFCheckWasSuccessful, userAlreadyHasRegisteredFace, userHasBiometry, hasSsoAuth,
    renewToken, updateUser, handleAuthenticatedUser, getUserByEmailOrDocument, errorWhenTryingToRegisterRf,
    ssoAuth, setSsoAuth
  }), [
    user, loading, authenticatedUser, rfData, getFacialRecognitionUrl, tryAgainRC,
    pollFacialRecognitionStatus, rFCheckWasSuccessful, userAlreadyHasRegisteredFace, userHasBiometry, hasSsoAuth,
    renewToken, updateUser, handleAuthenticatedUser, getUserByEmailOrDocument, errorWhenTryingToRegisterRf,
    ssoAuth, setSsoAuth
  ]);

  return (
    <UserContext.Provider value={shared}>
      {children}
    </UserContext.Provider>
  );
}

UserProvider.displayName = 'UserProvider';
export const useUserContext = (): UserContextProps => useContext(UserContext);

export { UserContext, UserProvider };
